import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import type { ColumnDef } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { reportTenderStyle } from '@/components/reports/report-tender-styles'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { REPORT_PIE_CHART_COLORS, formatPieSharePercent } from '../-helpers'
import { CustomerMonogram, ProductThumbnail } from './-avatars'
import {
  type SalesStatisticsProductRow,
  formatQuantity,
  getCustomerToneKey,
  getProductActivityAmount,
  getProductSignedAmount,
} from './-helpers'
import { type PathLink, type PathSearchParams, usePathHelper } from '@/hooks/usePathHelper'
import type {
  SalesSummaryReportCustomerRow,
  SalesSummaryReportOrganizationRow,
  SalesSummaryReportPaymentMethodRow,
  SalesSummaryReportProductRow,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { SectionCardHeader } from './-section-card-header'

const LEADERBOARD_LIMIT = 3
const STATISTICS_LEADERBOARD_LIMIT = 5
const OTHER_COLOR = 'var(--color-muted)'

/** One leaderboard entry: a top row (`item`) or everything past the limit (`other`). */
type LeaderboardChartRowBase = {
  key: string
  chartKey: string
  fill: string | undefined
  label: string
  shortLabel: string
  amount: number
  share: number
  sharePercent: number
}

type ProductChartRow = LeaderboardChartRowBase & {
  quantity: number
  refundedQuantity: number
} & (
    | {
        kind: 'item'
        imageUrl: string | null
        product_id: number | null
        product_deleted: boolean
        isCustom: boolean
      }
    | { kind: 'other' }
  )

type CustomerChartRow = LeaderboardChartRowBase & {
  orders: number
  refunds: number
} & ({ kind: 'item'; customer_id: number | null } | { kind: 'other' })

type TopProductsCardProps = {
  productRows: SalesSummaryReportProductRow[]
  isOrderBased?: boolean
  className?: string
  title?: string
  description?: string | null
  emptyMessage?: string
}

export function TopProductsCard({
  productRows,
  isOrderBased = false,
  className,
  title = 'Top Products',
  description = 'Products with the largest sales impact',
  emptyMessage = 'No products in the selected range.',
}: TopProductsCardProps) {
  const path = usePathHelper()
  const limit = isOrderBased ? STATISTICS_LEADERBOARD_LIMIT : LEADERBOARD_LIMIT
  const rankedRows = isOrderBased
    ? productRows.filter((row) => parseCurrency(row.net_total) !== 0)
    : productRows
  // Statistics ranks categories by net sales and sizes them by its amount; legacy views rank and
  // size by total activity.
  const sortedProductRows = sortRowsByActivity(
    rankedRows,
    isOrderBased ? getProductSignedAmount : getProductActivityAmount
  )
  const topProductRows = sortedProductRows.slice(0, limit)
  const countLabel = topProductRows.length ? `Top ${topProductRows.length}` : '0 products'
  const chartRows = buildProductChartRows(
    sortedProductRows,
    limit,
    isOrderBased ? (row) => Math.abs(getProductSignedAmount(row)) : getProductActivityAmount
  ).filter((row) => row.kind !== 'other')

  return (
    <Card className={cn('min-w-0', className)}>
      <SectionCardHeader
        title={title}
        description={description}
        count={countLabel}
        action={
          isOrderBased && (
            <AllStatisticsRows
              title="By Product Type"
              rows={productRows}
              label="Product type / custom item"
              getKey={(row) => row.product_key}
              getName={(row) => row.product_name}
              getAmount={(row) => row.net_total}
              isCustom={(row) => row.product_key.startsWith('custom:')}
            />
          )
        }
      />
      <CardContent className="space-y-4">
        {topProductRows.length ? (
          <div className="space-y-3">
            {chartRows.map((row) => {
              const productLink =
                row.product_id && !row.product_deleted
                  ? path.products.show(row.product_id).link
                  : null

              return (
                <TopProductRow
                  key={row.key}
                  row={row}
                  title={
                    productLink ? (
                      <Link
                        {...productLink}
                        className="truncate text-sm font-medium text-foreground transition-colors hover:text-interaction hover:underline"
                      >
                        {row.label}
                      </Link>
                    ) : (
                      <span className="truncate text-sm font-medium text-foreground">
                        {row.label}
                      </span>
                    )
                  }
                  badges={
                    <>
                      {row.product_deleted ? (
                        <Badge variant="muted" size="xs">
                          Deleted
                        </Badge>
                      ) : null}
                      {row.isCustom ? (
                        <Badge variant="muted" size="xs">
                          Custom
                        </Badge>
                      ) : null}
                    </>
                  }
                />
              )
            })}
          </div>
        ) : (
          <Typography variant="supporting">{emptyMessage}</Typography>
        )}
      </CardContent>
    </Card>
  )
}

type KeyCustomersCardProps = {
  /** Already ranked by activity. */
  customerRows: SalesSummaryReportCustomerRow[]
  orgId: number
  className?: string
  isOrderBased?: boolean
}

export function KeyCustomersCard({
  customerRows,
  orgId,
  className,
  isOrderBased = false,
}: KeyCustomersCardProps) {
  const path = usePathHelper()
  const limit = isOrderBased ? STATISTICS_LEADERBOARD_LIMIT : LEADERBOARD_LIMIT
  const topCustomerRows = customerRows.slice(0, limit)
  const customerCountLabel = topCustomerRows.length
    ? `Top ${topCustomerRows.length}`
    : '0 customers'
  const chartRows = buildCustomerChartRows(customerRows, limit)

  return (
    <Card className={cn('min-w-0', className)}>
      <SectionCardHeader
        title={isOrderBased ? 'By Customer' : 'Key Customers'}
        description={isOrderBased ? undefined : 'Amounts paid toward orders'}
        action={
          isOrderBased && (
            <AllStatisticsRows
              title="By Customer"
              rows={customerRows}
              label="Customer"
              getKey={(row) => row.customer_key}
              getName={(row) => row.customer_name}
              getAmount={(row) => row.total_sales}
              getLink={(row) =>
                row.customer_id ? path.customers.show(row.customer_id).using({ orgId }).link : null
              }
            />
          )
        }
        count={customerCountLabel}
      />
      <CardContent className="min-w-0 space-y-4">
        {topCustomerRows.length ? (
          <div className="space-y-3">
            {chartRows
              .filter((row) => row.kind !== 'other')
              .map((row) => {
                const customerLink = row.customer_id
                  ? path.customers.show(row.customer_id).using({ orgId }).link
                  : null

                return (
                  <CustomerRevenueShareRow
                    key={row.key}
                    accentColor={row.fill}
                    leading={
                      <CustomerMonogram
                        name={row.label}
                        toneKey={getCustomerToneKey(row.customer_id, row.label)}
                        size="sm"
                      />
                    }
                    title={
                      customerLink ? (
                        <Link
                          {...customerLink}
                          className="truncate text-sm font-medium text-foreground transition-colors hover:text-interaction hover:underline"
                        >
                          {row.label}
                        </Link>
                      ) : (
                        <span className="truncate text-sm font-medium text-foreground">
                          {row.label}
                        </span>
                      )
                    }
                    details={`${isOrderBased ? formatOrderCount(row.orders) : formatCustomerActivity(row)} · ${formatPieSharePercent(row.share)} of activity`}
                    value={formatCurrency(row.amount)}
                    sharePercent={row.sharePercent}
                  />
                )
              })}
          </div>
        ) : (
          <Typography variant="supporting">No customers in the selected range.</Typography>
        )}
      </CardContent>
    </Card>
  )
}

export function PaymentMethodsCard({
  rows,
  isOrderBased = false,
}: {
  rows: SalesSummaryReportPaymentMethodRow[]
  isOrderBased?: boolean
}) {
  return (
    <Card className="min-w-0">
      <SectionCardHeader title={isOrderBased ? 'By Payment Method' : 'How money came in'} />
      <CardContent className="space-y-3">
        {rows.length ? (
          <>
            {rows.map((row) => {
              const method = reportTenderStyle(row.payment_type)
              return (
                <LeaderboardEntryRow
                  key={row.payment_type}
                  accentColor={method.color}
                  leading={<StyledIcon icon={method.icon} tone={method.tone} aria-hidden="true" />}
                  title={<Typography variant="label">{row.label}</Typography>}
                  details={`${formatPieSharePercent(row.share_of_activity)} of activity`}
                  value={formatCurrency(row.total_collected)}
                  sharePercent={Number(row.share_of_activity) * 100}
                />
              )
            })}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <Typography variant="labelStrong">
                {isOrderBased ? 'Total sales' : 'Total collected'}
              </Typography>
              <Typography variant="labelStrong" className="tabular-nums">
                {formatCurrency(
                  rows.reduce((sum, row) => sum + parseCurrency(row.total_collected), 0)
                )}
              </Typography>
            </div>
          </>
        ) : (
          <Typography variant="supporting">Payment method breakdown unavailable.</Typography>
        )}
      </CardContent>
    </Card>
  )
}

type TopOrganizationsCardProps = {
  organizationRows: SalesSummaryReportOrganizationRow[]
  /** The report's dates, carried to each florist's own report. */
  reportDateSearch: PathSearchParams | null
  className?: string
}

export function TopOrganizationsCard({
  organizationRows,
  reportDateSearch,
  className,
}: TopOrganizationsCardProps) {
  const path = usePathHelper()
  const topOrganizationRows = organizationRows.slice(0, LEADERBOARD_LIMIT)
  const countLabel = topOrganizationRows.length ? `Top ${LEADERBOARD_LIMIT}` : '0 florists'

  return (
    <Card className={cn('min-w-0', className)}>
      <SectionCardHeader
        title="Top Florists"
        description="Florist organizations with the largest sales impact"
        count={countLabel}
      />
      <CardContent className="min-w-0 space-y-4">
        {topOrganizationRows.length ? (
          <div className="space-y-3">
            {topOrganizationRows.map((row, index) => {
              const floristLink = row.organization_id
                ? path.salesStatistics
                    .index()
                    .using({ orgId: row.organization_id })
                    .search(reportDateSearch ?? {}).link
                : null

              return (
                <CustomerRevenueShareRow
                  key={row.organization_id ?? row.organization_name}
                  accentColor={REPORT_PIE_CHART_COLORS[index % REPORT_PIE_CHART_COLORS.length]}
                  leading={
                    <CustomerMonogram
                      name={row.organization_name}
                      toneKey={row.organization_id ?? row.organization_name}
                      size="sm"
                    />
                  }
                  title={
                    floristLink ? (
                      <Link
                        {...floristLink}
                        className="truncate text-sm font-medium text-foreground transition-colors hover:text-interaction hover:underline"
                      >
                        {row.organization_name}
                      </Link>
                    ) : (
                      <span className="truncate text-sm font-medium text-foreground">
                        {row.organization_name}
                      </span>
                    )
                  }
                  details={`${formatOrganizationActivity(row)} · ${formatPieSharePercent(row.share_of_total)} of activity`}
                  value={formatCurrency(row.total_sales)}
                  sharePercent={Number(row.share_of_total ?? 0) * 100}
                />
              )
            })}
          </div>
        ) : (
          <Typography variant="supporting">No florist activity in the selected range.</Typography>
        )}
      </CardContent>
    </Card>
  )
}

type LeaderboardEntryRowProps = {
  accentColor: string | undefined
  leading: ReactNode
  title: ReactNode
  details: string
  value: string
  sharePercent: number
}

function CustomerRevenueShareRow({
  accentColor,
  leading,
  title,
  details,
  value,
  sharePercent,
}: LeaderboardEntryRowProps) {
  return (
    <LeaderboardEntryRow
      accentColor={accentColor}
      leading={leading}
      title={title}
      details={details}
      value={value}
      sharePercent={sharePercent}
    />
  )
}

function TopProductRow({
  row,
  title,
  badges,
}: {
  row: Extract<ProductChartRow, { kind: 'item' }>
  title: ReactNode
  badges: ReactNode
}) {
  return (
    <LeaderboardEntryRow
      accentColor={row.fill}
      leading={<ProductThumbnail imageUrl={row.imageUrl} name={row.label} />}
      title={
        <>
          {title}
          {badges}
        </>
      }
      details={`${formatProductActivity(row)} · ${formatPieSharePercent(row.share)} of activity`}
      value={formatCurrency(row.amount)}
      sharePercent={row.sharePercent}
    />
  )
}

function LeaderboardEntryRow({
  accentColor,
  leading,
  title,
  details,
  value,
  sharePercent,
}: LeaderboardEntryRowProps) {
  return (
    <Card surface="subtle" className="@container/leaderboard-entry min-h-[88px] px-4 py-3">
      <div className="flex min-h-[64px] min-w-0 items-center gap-3">
        <LeaderboardShareRing
          sharePercent={sharePercent}
          color={accentColor}
          label={`${Math.round(Math.max(0, Number(sharePercent ?? 0)))}%`}
        />
        {leading}
        <div className="grid min-w-0 flex-1 gap-1 @sm/leaderboard-entry:grid-cols-[minmax(0,1fr)_auto] @sm/leaderboard-entry:gap-x-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">{title}</div>
          <Typography
            variant="labelStrong"
            className="tabular-nums @sm/leaderboard-entry:text-right"
          >
            {value}
          </Typography>
          <Typography variant="caption" className="block @sm/leaderboard-entry:col-span-2">
            {details}
          </Typography>
        </div>
      </div>
    </Card>
  )
}

function LeaderboardShareRing({
  sharePercent,
  color,
  label,
}: {
  sharePercent: number
  color: string | undefined
  label: string
}) {
  const normalizedPercent = Math.max(0, Math.min(100, Number(sharePercent ?? 0)))
  const radius = 24
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (normalizedPercent / 100) * circumference

  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
      <svg viewBox="0 0 64 64" className="h-14 w-14 -rotate-90">
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="color-mix(in srgb, var(--border) 70%, transparent)"
          strokeWidth="6"
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke={color}
          strokeLinecap="round"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span className="absolute text-[11px] font-semibold tabular-nums text-foreground">
        {label}
      </span>
    </div>
  )
}

function buildProductChartRows(
  rows: SalesSummaryReportProductRow[],
  limit = LEADERBOARD_LIMIT,
  getActivityAmount: (row: SalesStatisticsProductRow) => number = getProductActivityAmount
): ProductChartRow[] {
  const totalActivity = rows.reduce((sum, row) => sum + getActivityAmount(row), 0)
  const topRows = rows.slice(0, limit)
  const otherRows = rows.slice(limit)
  const otherQuantity = otherRows.reduce((sum, row) => sum + Number(row.sold_quantity ?? 0), 0)
  const otherRefundedQuantity = otherRows.reduce(
    (sum, row) => sum + Number(row.refunded_quantity ?? 0),
    0
  )
  const otherActivity = otherRows.reduce((sum, row) => sum + getActivityAmount(row), 0)
  const otherAmount = otherRows.reduce((sum, row) => sum + getProductSignedAmount(row), 0)

  const mappedTopRows = topRows.map(
    (row, index): ProductChartRow => ({
      kind: 'item',
      key: row.product_key,
      chartKey: `leaderboard-${index + 1}`,
      fill: REPORT_PIE_CHART_COLORS[index % REPORT_PIE_CHART_COLORS.length],
      label: row.product_name,
      shortLabel: truncateLeaderboardLabel(row.product_name),
      quantity: Number(row.sold_quantity ?? 0),
      refundedQuantity: Number(row.refunded_quantity ?? 0),
      amount: getProductSignedAmount(row),
      share: totalActivity > 0 ? getActivityAmount(row) / totalActivity : 0,
      sharePercent: totalActivity > 0 ? (getActivityAmount(row) / totalActivity) * 100 : 0,
      imageUrl: row.product_image_url,
      product_id: row.product_id,
      product_deleted: row.product_deleted,
      isCustom: row.product_key?.startsWith?.('custom:'),
    })
  )

  if (otherActivity > 0) {
    mappedTopRows.push({
      kind: 'other',
      key: 'other-products',
      chartKey: 'leaderboard-other',
      fill: OTHER_COLOR,
      label: 'Other',
      shortLabel: 'Other',
      quantity: otherQuantity,
      refundedQuantity: otherRefundedQuantity,
      amount: otherAmount,
      share: totalActivity > 0 ? otherActivity / totalActivity : 0,
      sharePercent: totalActivity > 0 ? (otherActivity / totalActivity) * 100 : 0,
    })
  }

  return mappedTopRows
}

function buildCustomerChartRows(
  rows: SalesSummaryReportCustomerRow[],
  limit = LEADERBOARD_LIMIT
): CustomerChartRow[] {
  const totalActivity = rows.reduce((sum, row) => sum + getCustomerActivityAmount(row), 0)
  const topRows = rows.slice(0, limit)
  const otherRows = rows.slice(limit)
  const otherSales = otherRows.reduce((sum, row) => sum + parseCurrency(row.total_sales), 0)
  const otherOrders = otherRows.reduce((sum, row) => sum + Number(row.order_count ?? 0), 0)
  const otherRefunds = otherRows.reduce((sum, row) => sum + Number(row.refund_count ?? 0), 0)
  const otherActivity = otherRows.reduce((sum, row) => sum + getCustomerActivityAmount(row), 0)

  const mappedTopRows = topRows.map((row, index): CustomerChartRow => {
    return {
      kind: 'item',
      key: row.customer_key,
      chartKey: `leaderboard-${index + 1}`,
      fill: REPORT_PIE_CHART_COLORS[index % REPORT_PIE_CHART_COLORS.length],
      label: row.customer_name,
      shortLabel: truncateLeaderboardLabel(row.customer_name),
      orders: Number(row.order_count ?? 0),
      refunds: Number(row.refund_count ?? 0),
      amount: parseCurrency(row.total_sales),
      share: totalActivity > 0 ? getCustomerActivityAmount(row) / totalActivity : 0,
      sharePercent: totalActivity > 0 ? (getCustomerActivityAmount(row) / totalActivity) * 100 : 0,
      customer_id: row.customer_id,
    }
  })

  if (otherActivity > 0) {
    mappedTopRows.push({
      kind: 'other',
      key: 'other-customers',
      chartKey: 'leaderboard-other',
      fill: OTHER_COLOR,
      label: 'Other',
      shortLabel: 'Other',
      orders: otherOrders,
      refunds: otherRefunds,
      amount: otherSales,
      share: totalActivity > 0 ? otherActivity / totalActivity : 0,
      sharePercent: totalActivity > 0 ? (otherActivity / totalActivity) * 100 : 0,
    })
  }

  return mappedTopRows
}

function sortRowsByActivity(
  rows: SalesSummaryReportProductRow[],
  getActivityAmount: (row: SalesStatisticsProductRow) => number
) {
  return [...rows].sort((left, right) => {
    const activityDifference = getActivityAmount(right) - getActivityAmount(left)

    if (activityDifference !== 0) {
      return activityDifference
    }

    return getProductSignedAmount(right) - getProductSignedAmount(left)
  })
}

function getCustomerActivityAmount(row: SalesSummaryReportCustomerRow) {
  return Math.abs(parseCurrency(row.total_sales))
}

function formatProductActivity(row: ProductChartRow) {
  const parts: (string | null)[] = []

  if (Number(row.quantity ?? 0) > 0) {
    parts.push(formatProductActivityLabel(row.quantity, 'sold'))
  }

  if (Number(row.refundedQuantity ?? 0) > 0) {
    parts.push(formatProductActivityLabel(row.refundedQuantity, 'refunded'))
  }

  if (parts.length) {
    return parts.join(' · ')
  }

  if (Number(row.amount ?? 0) < 0) {
    return 'Refund activity'
  }

  if (Number(row.amount ?? 0) > 0) {
    return 'Sales activity'
  }

  return 'Cash activity'
}

function formatProductActivityLabel(quantity: number, actionLabel: string) {
  const numericQuantity = Number(quantity ?? 0)

  if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
    return null
  }

  return `${formatQuantity(numericQuantity)} ${actionLabel}`
}

function formatOrderCount(value: number) {
  const orderCount = Number(value ?? 0)

  return `${formatQuantity(orderCount)} ${orderCount === 1 ? 'order' : 'orders'}`
}

function formatRefundCount(value: number) {
  const refundCount = Number(value ?? 0)

  return `${formatQuantity(refundCount)} ${refundCount === 1 ? 'refund' : 'refunds'}`
}

function formatCustomerActivity(row: CustomerChartRow) {
  const parts: string[] = []

  if (Number(row.orders ?? 0) > 0) {
    parts.push(formatOrderCount(row.orders))
  }

  if (Number(row.refunds ?? 0) > 0) {
    parts.push(formatRefundCount(row.refunds))
  }

  return parts.length ? parts.join(' · ') : 'No order activity'
}

function formatOrganizationActivity(row: SalesSummaryReportOrganizationRow) {
  const parts: string[] = []

  if (Number(row.order_count ?? 0) > 0) {
    parts.push(formatOrderCount(row.order_count))
  }

  if (Number(row.refund_count ?? 0) > 0) {
    parts.push(formatRefundCount(row.refund_count))
  }

  return parts.length ? parts.join(' · ') : 'No order activity'
}

function truncateLeaderboardLabel(value: string, maxLength = 16) {
  if (!value) {
    return ''
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
}

type AllStatisticsRowsProps<Row> = {
  title: string
  rows: Row[]
  label: string
  getKey: (row: Row) => string
  getName: (row: Row) => string
  /** API money. */
  getAmount: (row: Row) => string
  getLink?: (row: Row) => PathLink | null
  /** A custom item, not a catalog product type. */
  isCustom?: (row: Row) => boolean
}

function AllStatisticsRows<Row>({
  title,
  rows,
  label,
  getKey,
  getName,
  getAmount,
  getLink,
  isCustom,
}: AllStatisticsRowsProps<Row>) {
  const columns: ColumnDef<Row>[] = [
    {
      id: 'name',
      accessorFn: getName,
      header: label,
      meta: { label, kind: 'primary' },
      cell: ({ row }) => {
        const link = getLink?.(row.original)
        return (
          <div className="flex flex-wrap items-center gap-2">
            {link ? (
              <Typography
                as={Link}
                {...link}
                variant="bodySmall"
                tone="interaction"
                interaction="hoverUnderline"
              >
                {getName(row.original)}
              </Typography>
            ) : (
              <Typography variant="bodySmall">{getName(row.original)}</Typography>
            )}
            {isCustom?.(row.original) && (
              <Badge variant="muted" size="xs">
                Custom
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      id: 'amount',
      accessorFn: getAmount,
      header: 'Sales',
      cell: ({ row }) => (
        <Typography variant="label" className="tabular-nums whitespace-nowrap">
          {formatCurrency(getAmount(row.original))}
        </Typography>
      ),
      meta: { label: 'Sales', kind: 'money', align: 'end' },
    },
  ]
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link" size="sm">
          View all
        </Button>
      </DialogTrigger>
      <DialogContent size="xlarge" viewport="scroll">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>For the selected date range.</DialogDescription>
        </DialogHeader>
        <IndexDataTable
          mode="client"
          data={rows}
          columns={columns}
          getRowId={getKey}
          initialSorting={[{ id: 'amount', desc: true }]}
          pagination
          defaultPageSize={10}
          scrollAreaClassName="max-h-[55vh]"
        />
        <div className="flex justify-between">
          <Typography variant="labelStrong">Total</Typography>
          <Typography variant="labelStrong" className="tabular-nums">
            {formatCurrency(rows.reduce((sum, row) => sum + parseCurrency(getAmount(row)), 0))}
          </Typography>
        </div>
      </DialogContent>
    </Dialog>
  )
}
